import type { Edge, Node } from '@xyflow/react'
import { parsePhoneNumber } from 'libphonenumber-js'
import { omit, unset } from 'lodash'
import { generateWhatsAppPayload } from 'v2/pages/conversations/outbound/utils'
import { getTemplate } from 'v2/pages/conversations/utils/transformers'
import { FlowTypes } from 'v2/types'
import { CALL_PLACING_NODE_TYPES, SCREENING_NODE_TYPES } from '../config/contact-screening.config'
import { actionIntentInitialState, intentInitialState } from '../config/nodes.config'
import { useFlowStore } from '../store'
import {
  type AgentPresetConversationNodeData,
  type AiAgentChatNodeData,
  type AiAgentV2NodeData,
  type AiAgentWhatsappNodeData,
  FlowNodeTypes,
} from '../types'
import { isAiAgentV2Type } from './ai-agent-v29'
import { NodeValidationError } from './flow-validation'

/**
 * Default configuration for WhatsApp templates
 */
export const defaultWhatsappTemplateConfig: {
  template: Record<string, unknown>
  whatsapp_template: Record<string, unknown>
  variables: {
    header: string[]
    body: string[]
  }
} = {
  template: {},
  whatsapp_template: {},
  variables: {
    header: [],
    body: [],
  },
}

/**
 * Extract header and body variables from whatsapp_template components
 * Handles different parameter types: text, image, video, document
 * @param components Template components from whatsapp_template
 * @returns Object with header and body variable arrays
 */
export function extractTemplateVariables(
  components?: Array<{
    type: string
    parameters?: Array<{
      text?: string
      type?: string
      image?: { link?: string }
      video?: { link?: string }
      document?: { link?: string }
    }>
  }>,
) {
  const variables = {
    header: [] as string[],
    body: [] as string[],
  }

  if (!components) return variables

  components.forEach((component) => {
    if (component.type === 'header' && component.parameters) {
      variables.header = component.parameters.map((p) => {
        // Extract URL from media parameters
        if (p.type === 'image' && p.image?.link) return p.image.link
        if (p.type === 'video' && p.video?.link) return p.video.link
        if (p.type === 'document' && p.document?.link) return p.document.link
        // Extract text from text parameters
        if (p.type === 'text' && p.text) return p.text
        return ''
      })
    }
    if (component.type === 'body' && component.parameters) {
      variables.body = component.parameters.map((p) => p.text || '')
    }
  })

  return variables
}

/**
 * Format AI agent WhatsApp node data
 * @param data Node data from backend
 * @returns Formatted data for frontend
 */
export function formatAiAgentWhatsappData(data: Record<string, unknown>) {
  const whatsappData = data as {
    disable_inequipped_to_serve: boolean
    whatsapp_msg_type:
      | 'template'
      | 'custom'
      | 'text'
      | 'whatsapp_media'
      | 'whatsapp_button'
      | 'whatsapp_list'
    whatsapp_template: {
      template_uuid: string
      name?: string
      namespace?: string
      language?: { code: string; policy?: string }
      components?: Array<{
        type: string
        parameters?: Array<{
          text?: string
          type?: string
          image?: { link?: string }
          video?: { link?: string }
          document?: { link?: string }
        }>
      }>
    }
    whatsapp_media?: Array<{
      name?: string
      media_type?: string
      url?: string
    }>
    whatsapp_list?: {
      header?: string
      body?: string
      footer?: string
      button?: string
      sections?: unknown[]
    }
  }
  const disableInequippedToServe = whatsappData.disable_inequipped_to_serve ?? false
  const defaultConfig = {
    ...defaultWhatsappTemplateConfig,
    disable_inequipped_to_serve: disableInequippedToServe,
  }

  // BACKWARD COMPATIBILITY: Handle 'custom' whatsapp_msg_type
  if (whatsappData.whatsapp_msg_type === 'custom') {
    // Check if media exists and is valid
    if (
      whatsappData.whatsapp_media &&
      Array.isArray(whatsappData.whatsapp_media) &&
      whatsappData.whatsapp_media.length > 0
    ) {
      // Validate media items
      const isValidMedia = whatsappData.whatsapp_media.every(
        (mediaItem) => mediaItem?.name && mediaItem?.media_type && mediaItem?.url,
      )

      // Update whatsapp_msg_type based on media validity
      const updatedData = {
        ...data,
        whatsapp_msg_type: isValidMedia ? 'whatsapp_media' : 'text',
      }
      return {
        ...updatedData,
        ...defaultConfig,
      }
    }

    // If no valid media, treat as text
    return {
      ...data,
      whatsapp_msg_type: 'text',
      ...defaultConfig,
    }
  }

  if (whatsappData.whatsapp_msg_type === 'whatsapp_button') {
    // Handle both new format (whatsapp_button.header) and old flattened format (header)
    const legacyData = data as Record<string, unknown>
    const whatsappButton = (legacyData.whatsapp_button ?? {}) as {
      header?: { type?: string; text?: string; media?: Record<string, unknown> }
      body?: string
      footer?: string
      buttons?: unknown[]
    }
    const header = (whatsappButton?.header ?? legacyData.header ?? {}) as {
      type?: string
      text?: string
      media?: Record<string, unknown>
    }
    const dataToReturn = {
      ...defaultConfig,
      ...data,
      header_type: header.type || 'none',
      header_text: header.text ?? '',
      header_media: header.media && header.media?.url !== '' ? header.media : null,
      body: whatsappButton?.body ?? legacyData.body ?? '',
      footer: whatsappButton?.footer ?? legacyData.footer ?? '',
      buttons: whatsappButton?.buttons ?? legacyData.buttons ?? [],
    }
    return dataToReturn
  }

  // Add specific handling for whatsapp_list type during data formatting
  if (whatsappData.whatsapp_msg_type === 'whatsapp_list') {
    // Construct whatsapp_list object from existing data or top-level fields (backward compatibility)
    // Cast to include legacy flat fields that may exist from old backend format
    const legacyData = data as Record<string, unknown>
    const existingList = whatsappData.whatsapp_list || {}
    const whatsappList = {
      header: existingList.header ?? legacyData.header ?? null,
      body: existingList.body ?? legacyData.body ?? '',
      footer: existingList.footer ?? legacyData.footer ?? null,
      button: existingList.button ?? legacyData.button ?? '',
      sections: existingList.sections ?? legacyData.sections ?? [],
    }
    const listData = {
      ...defaultConfig,
      ...data,
      whatsapp_list: whatsappList,
      sections: whatsappList.sections,
    }
    return listData
  }

  // Process template data if it exists
  if (whatsappData.whatsapp_template?.template_uuid) {
    // Try to get full template from cache (backend might not have loaded templates yet)
    const cachedTemplate = getTemplate(whatsappData.whatsapp_template?.template_uuid)

    // Extract variables from whatsapp_template components
    const extractedVariables = extractTemplateVariables(whatsappData.whatsapp_template?.components)
    defaultConfig.variables = extractedVariables

    // Use cached template if available, otherwise reconstruct minimal template from backend data
    const templateToUse = cachedTemplate || {
      uuid: whatsappData.whatsapp_template.template_uuid,
      data: {
        name: whatsappData.whatsapp_template.name || '',
        language: whatsappData.whatsapp_template.language?.code || '',
        namespace: whatsappData.whatsapp_template.namespace || '',
        components: whatsappData.whatsapp_template.components || [],
        status: 'approved',
      },
    }

    return {
      ...defaultConfig,
      ...data,
      template: templateToUse,
    }
  }

  // Special handling for AI agent conversation type
  if (data.conversation_type === 'ai_agent') {
    return {
      ...omit(data, ['whatsapp_msg_type', 'content_type', 'text', 'whatsapp_media', 'to', 'from']),
      ...defaultConfig,
    }
  }

  // Default return - defaultConfig takes precedence to ensure WhatsApp template
  // configuration always has the required structure, even if input data contains
  // partial or invalid template properties
  return {
    ...data,
    ...defaultConfig,
  }
}

/**
 * Format AI agent chat node data
 * @param data Node data from backend
 * @returns Formatted data for frontend
 */
export function formatAiAgentChatData(data: Record<string, unknown>) {
  const aiAgentChatData = data as {
    disable_inequipped_to_serve: boolean
    content_type: 'text' | 'chat_media'
    text: string
    chat_media: Record<string, never>
    chat_button: {
      header: {
        name: string
        media_type: string
        url: string
        size: number
      }
      title: string
      description: string
      buttons: Array<{
        button_type: string
        call_phone: string
        website_url: string
        call_country: string
      }>
    }
  }

  if (aiAgentChatData.content_type === 'text') {
    aiAgentChatData.chat_media = {}
  } else {
    aiAgentChatData.text = ''
  }

  return {
    ...aiAgentChatData,
    disable_inequipped_to_serve: aiAgentChatData.disable_inequipped_to_serve ?? false,
    header_media: aiAgentChatData.chat_button?.header?.url
      ? aiAgentChatData.chat_button?.header
      : undefined,
    title: aiAgentChatData.chat_button?.title,
    button_description: aiAgentChatData.chat_button?.description,
    buttons: aiAgentChatData.chat_button?.buttons.map((button) => ({
      ...button,
      call_phone:
        button.call_phone && button.call_country
          ? `+${button.call_country}${button.call_phone}`
          : undefined,
    })),
  }
}

// Define types to avoid TypeScript errors

interface ButtonType {
  button_type: string
  call_phone?: string
  website_url?: string
  call_country?: string
}

interface HeaderMediaType {
  url?: string
  name?: string
  media_type?: string
  size?: number
}

/**
 * Transform AI agent chat node data for backend
 * @param config Node configuration
 * @returns Transformed data for backend
 */
export function transformAiAgentChatConfig(
  config: AiAgentChatNodeData['config'] | AgentPresetConversationNodeData['config'],
): Record<string, unknown> {
  // Create a copy to avoid mutating the original
  const configCopy = { ...config }

  if (configCopy.content_type === 'text') {
    delete configCopy.chat_media
  } else {
    delete configCopy.text
  }

  // Type assertion for header_media and buttons to avoid TypeScript errors
  const headerMedia = configCopy.header_media as HeaderMediaType | undefined
  const buttons = configCopy.buttons as ButtonType[] | undefined

  return {
    ...configCopy,
    chat_button:
      configCopy.content_type === 'chat_button'
        ? {
            title: configCopy.title,
            description: configCopy.button_description,
            ...(headerMedia?.url ? { header: headerMedia } : {}),
            buttons:
              buttons?.map((button) => {
                if (!button.call_phone) {
                  return button
                }
                const phoneNumber = parsePhoneNumber(button.call_phone)
                return {
                  ...button,
                  call_phone: phoneNumber.nationalNumber,
                  call_country: phoneNumber.countryCallingCode,
                }
              }) || [],
          }
        : null,
  }
}

/**
 * Transform AI agent WhatsApp node data for backend
 * @param config Node configuration
 * @returns Transformed data for backend
 */
export function transformAiAgentWhatsappConfig(
  config: AiAgentWhatsappNodeData['config'] | AgentPresetConversationNodeData['config'],
  nodeId?: string,
  nodeName?: string,
): Record<string, unknown> {
  // Clone the config to avoid mutating the original
  const configCopy = { ...config }

  if (configCopy.whatsapp_msg_type === 'whatsapp_list') {
    return {
      ...configCopy,
      whatsapp_list: {
        ...configCopy.whatsapp_list,
        sections: configCopy.sections,
      },
    }
  }

  // Remove whatsapp_list from config if it's not a whatsapp_list message
  unset(configCopy, 'whatsapp_list')

  if (configCopy.whatsapp_msg_type === 'whatsapp_button') {
    const headerType = configCopy.header_type || 'none'
    return {
      ...configCopy,
      whatsapp_button: {
        header: {
          type: headerType,
          text: configCopy.header_text,
          media: headerType === 'media' ? configCopy.header_media : null,
        },
        body: configCopy.body,
        footer: configCopy.footer,
        buttons: configCopy.buttons,
      },
    }
  }

  if (configCopy.whatsapp_msg_type === 'template') {
    // Validate template structure before calling generateWhatsAppPayload
    const { template: whatsappTemplate } = configCopy
    if (!whatsappTemplate || !whatsappTemplate.data || !whatsappTemplate.data.language) {
      if (nodeId && nodeName) {
        throw new NodeValidationError(
          'Template is required when WhatsApp message type is template. Please select a template.',
          nodeId,
          nodeName,
          ['template'],
        )
      } else {
        throw new Error(
          'Template is required when WhatsApp message type is template. Please select a template.',
        )
      }
    }

    const payload = generateWhatsAppPayload({
      template: configCopy.template,
      variables: configCopy.variables || { header: [], body: [] },
      from: configCopy.from || '',
      to: configCopy.to || '',
    })

    const { variables: _, template: __, ...rest } = configCopy
    return {
      ...rest,
      whatsapp_template: payload.template_attributes,
    }
  }

  // Delete fields when conversation_type is ai_generated
  if (configCopy.conversation_type === 'ai_generated') {
    return omit(configCopy, ['to', 'from'])
  }

  return configCopy
}

// THE "call-channel AI source" predicate — a node whose runtime is a
// call-channel AI conversation: a call ai_agent_v2, or any screening node
// (contact_screening is its own call-only wire component in the same AI
// family). Every consumer that branches on "is this a call AI node" must use
// this, not an inline check.
export function isCallAiSourceNode(node: Node | undefined): boolean {
  if (!node?.type) return false
  if (SCREENING_NODE_TYPES.has(node.type)) return true
  if (node.type === FlowNodeTypes.agent_node) return true // call-only by type
  return (
    isAiAgentV2Type(node.type) && (node as Node<AiAgentV2NodeData>).data?.config?.channel === 'call'
  )
}

// Walks predecessors from targetNodeId, hopping only through http_request nodes
// (the active call session survives an http_request, so it stays transparent here;
// no other node type is). True if any such path reaches a call-channel ai_agent_v2.
export function isConnectedToAiAgentV2Call(
  targetNodeId: string,
  nodes: Node[],
  edges: Edge[],
): boolean {
  const nodeMap = new Map(nodes.map((n) => [n.id, n]))
  const visited = new Set<string>([targetNodeId])
  const queue = [targetNodeId]

  for (let i = 0; i < queue.length; i++) {
    const currentId = queue[i]

    for (const edge of edges) {
      if (edge.target !== currentId) continue
      const source = nodeMap.get(edge.source)
      if (!source) continue

      if (isCallAiSourceNode(source)) return true
      if (source.type === FlowNodeTypes.http_request && !visited.has(edge.source)) {
        visited.add(edge.source)
        queue.push(edge.source)
      }
    }
  }
  return false
}

// An AI conversation on the call channel only makes sense once a call is active.
// A call becomes active either because the flow itself is a voice-call flow (the
// Start node IS the call) or because a call-placing node (Initiate Call, or the
// outbound screening composite embedding one) dials out earlier in the path.
// Mirrors checkPathToTriggers in sidebar/ai-agent-v2.tsx.
//
// The flowType call-flow check is load-bearing, not an optimisation: the walk
// only classifies call-placing nodes, so inbound call flows — where the trigger
// is the Start node, not a node in the graph — rely on this short-circuit.
//
// The walk is handle-agnostic: any call-placing ancestor counts regardless of
// which output branch (answered / no_answer / ...) the path took, matching
// checkPathToTriggers. `sourceNodeId` is included so connecting directly onto an
// Initiate Call counts.
export function hasCallTriggerUpstream(
  sourceNodeId: string | undefined,
  nodes: Node[],
  edges: Edge[],
  flowType?: FlowTypes,
): boolean {
  if (flowType === FlowTypes.incoming_call || flowType === FlowTypes.outgoing_call) return true
  if (!sourceNodeId) return false

  const nodeMap = new Map(nodes.map((n) => [n.id, n]))
  const visited = new Set<string>([sourceNodeId])
  const queue = [sourceNodeId]

  for (let i = 0; i < queue.length; i++) {
    const currentId = queue[i]
    if (CALL_PLACING_NODE_TYPES.has(nodeMap.get(currentId)?.type ?? '')) return true

    for (const edge of edges) {
      if (edge.target === currentId && !visited.has(edge.source)) {
        visited.add(edge.source)
        queue.push(edge.source)
      }
    }
  }
  return false
}

export function isAiAgentOrActionNode(nodeType?: string | null): boolean {
  return (
    isAiAgentV2Type(nodeType) ||
    nodeType === FlowNodeTypes.agent_node ||
    nodeType === FlowNodeTypes.ai_action
  )
}

export function isAiConversationNode(nodeType?: string | null): boolean {
  return (
    isAiAgentV2Type(nodeType) ||
    nodeType === FlowNodeTypes.agent_node ||
    nodeType === FlowNodeTypes.ai_action ||
    nodeType === FlowNodeTypes.ai_skills ||
    nodeType === FlowNodeTypes.ai_agent_call ||
    // contact_screening runs a call-channel AI conversation; its run-info
    // node_type is the wire type contact_screening (not ai_agent_v2 since the
    // recipe→component cutover), so the transcript renderer must recognize it.
    nodeType === FlowNodeTypes.contact_screening
  )
}

/**
 * Create and add an intent to an AI node using the flow store
 * @param nodeId The ID of the node to add the intent to
 * @returns The ID of the created intent, or null if the operation failed
 */
export function createAndAddIntentToAiNode(nodeId: string): string | null {
  const { flowInstance } = useFlowStore.getState()

  if (!flowInstance) return null

  const node = flowInstance.getNode(nodeId)
  if (!node || !node.type || !isAiAgentOrActionNode(node.type)) return null

  // Use different intent initial state based on node type
  const getIntentInitialState = () => {
    if (node.type === 'ai_action') return actionIntentInitialState()
    return intentInitialState()
  }
  const newIntent = getIntentInitialState()

  // Type assertion for AI nodes that have intents
  const aiNodeConfig = node.data.config as { intents?: (typeof newIntent)[] }
  const currentIntents = aiNodeConfig.intents || []

  // Update node data with new intent
  flowInstance.updateNodeData(nodeId, {
    config: {
      ...node.data.config,
      intents: [...currentIntents, newIntent],
    },
  } as Record<string, unknown>)

  return newIntent.id
}

/**
 * Filter intents in AI nodes (ai_agent_v2, ai_action) to only include those with specified IDs
 * @param node The node to filter intents for
 * @param allowedIntentIds Set of intent IDs to keep
 * @returns The node with filtered intents
 */
export function removeOrphanIntents<
  T extends { type?: string; data: { config?: Record<string, unknown> } },
>(node: T, allowedIntentIds: Set<string>): T {
  if (!node?.type || !isAiAgentOrActionNode(node.type)) return node
  if (!node?.data?.config) return node

  const config = node.data.config as Record<string, unknown> & { intents?: Array<{ id: string }> }
  if (!config?.intents) return node

  const filteredIntents = config.intents.filter(
    (intent) => intent?.id && allowedIntentIds.has(intent.id),
  )

  return {
    ...node,
    data: {
      ...node.data,
      config: {
        ...config,
        intents: filteredIntents,
      },
    },
  } as T
}

/**
 * Maps buttons to output states for dynamic flow connections
 * Filters buttons to only include 'reply' type buttons (or buttons without a specific type)
 * and creates output state objects with id and name based on button text/title
 *
 * @param buttons Array of button objects with text/title and optional button_type
 * @returns Array of output state objects for buttons
 */
export function mapButtonsToOutputStates(
  buttons?: Array<{
    text?: string
    title?: string
    button_type?: string
  }>,
): Array<{
  id: string
  name: string
  default: boolean
}> {
  if (!buttons) return []

  return buttons
    .filter((button) => {
      const text = button.text || button.title
      return text !== '' && (!button.button_type || button.button_type === 'reply')
    })
    .map((button) => ({
      id: button.text || button.title || '',
      name: button.text || button.title || '',
      default: false,
    }))
}
